import { FinanceType } from '@ph-core/enums/finance-type.enum';
import { calculateFilters } from '@ph-core/utils/fetch-filters';
import { CheckoutFormValues } from '@ph-model/checkout/checkout-form-values.model';
import { CreditInsuranceForm } from '@ph-model/form/credit-insurance-form';
import { Field } from '@ph-model/form/field';
import { FinanceForm } from '@ph-model/form/finance-form';
import { Markup } from '@ph-model/login';
import { GetProductsResponse } from '@ph-model/response';
import { CartItem, CartTaxFormValues, LoadQuote } from '@ph-shopping/model/cart';
import { calculateCustomerCosts, productResponseDataMapper } from '@ph-shopping/store/product/product.helpers';
import { VehicleStoreType, VehicleValues } from '@ph-shopping/store/vehicle/vehicle.store';

export const convertToByteArray = (input: string): BlobPart[] => {
  const sliceSize = 512;
  const bytes = [];

  for (let offset = 0; offset < input.length; offset += sliceSize) {
    const slice = input.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    bytes.push(byteArray);
  }

  return bytes;
};

export const getSynchronizedFinances = (
  checkoutFormValues: CheckoutFormValues,
  updatedFields: Partial<CheckoutFormValues>
): Partial<CheckoutFormValues> => {
  const oldFinanceForm = { ...checkoutFormValues.financeForm } as FinanceForm;
  const newFinanceForm = updatedFields.financeForm;
  const oldCreditInsuranceForm = { ...checkoutFormValues.creditInsuranceForm } as CreditInsuranceForm;
  const newCreditInsuranceForm = updatedFields.creditInsuranceForm as CreditInsuranceForm;

  const updatedFinance: Partial<FinanceForm> = {};
  const updatedCreditInsurance: Partial<CreditInsuranceForm> = {};

  if (newFinanceForm && !newCreditInsuranceForm) {
    if (newFinanceForm.financeAmount !== oldFinanceForm.financeAmount) {
      updatedCreditInsurance.loanAmount = newFinanceForm.financeAmount;
    }
    if (newFinanceForm.loanTerm !== oldFinanceForm.loanTerm) {
      updatedCreditInsurance.loanTerm = newFinanceForm.loanTerm;
    }
    if (newFinanceForm.amortizationPeriod !== oldFinanceForm.amortizationPeriod) {
      updatedCreditInsurance.amortizationPeriod = newFinanceForm.amortizationPeriod;
    }
    if (newFinanceForm.apr !== oldFinanceForm.apr) {
      updatedCreditInsurance.interestRate = newFinanceForm.apr;
    }
  }

  if (newCreditInsuranceForm && !newFinanceForm) {
    if (newCreditInsuranceForm.loanTerm !== oldCreditInsuranceForm.loanTerm) {
      updatedFinance.loanTerm = newCreditInsuranceForm.loanTerm;
    }
    if (newCreditInsuranceForm.amortizationPeriod !== oldCreditInsuranceForm.amortizationPeriod) {
      updatedFinance.amortizationPeriod = newCreditInsuranceForm.amortizationPeriod;
    }
    if (newCreditInsuranceForm.interestRate !== oldCreditInsuranceForm.interestRate) {
      updatedFinance.apr = newCreditInsuranceForm.interestRate;
    }
  }

  const stateToUpdate: Partial<CheckoutFormValues> = { ...updatedFields };

  if (Object.keys(updatedFinance).length > 0) {
    stateToUpdate.financeForm = { ...oldFinanceForm, ...updatedFinance };
  }
  if (Object.keys(updatedCreditInsurance).length > 0) {
    stateToUpdate.creditInsuranceForm = { ...oldCreditInsuranceForm, ...updatedCreditInsurance };
  }

  // insuranceTerm
  if (newCreditInsuranceForm) {
    if (oldCreditInsuranceForm.loanTerm !== newCreditInsuranceForm.loanTerm) {
      stateToUpdate.coverageInfoForm = {
        ...checkoutFormValues.coverageInfoForm,
        insuranceTerm: newCreditInsuranceForm.loanTerm,
      };
    }
  }

  return stateToUpdate;
};

export const calculateCartTotal = (cartTaxFormValues: CartTaxFormValues[]): number => {
  return cartTaxFormValues.reduce((total, item) => {
    const taxTotal = item.tax['total'] || item.tax['cost'];

    return total + taxTotal;
  }, 0);
};

export const recalculateTaxesAndTotal = (
  customerCost: number,
  customerTaxForm: Field[]
): Record<string, number> => {
  const formValues: Record<string, number> = {};
  let taxTotal = 0;

  for (const field of customerTaxForm) {
    if (field.name !== 'total' && field.taxPercentage) {
      const taxPercent = Number(field.taxPercentage) / 100;
      let newTax = +(customerCost * taxPercent).toFixed(2);
      if (isNaN(newTax)) {
        newTax = 0;
      }
      formValues[field.name] = newTax;
      taxTotal += newTax;
    }
  }

  formValues['cost'] = customerCost;
  formValues['total'] = Math.round((customerCost + taxTotal) * 100) / 100;

  return formValues;
};

// this is mapper for loadQuote api call
export const mapProductsFromLoadQuote = (payload: LoadQuote, markup: Markup, useQuoteOnlyEndpoint: boolean) => {
  const productState = payload.productState;

  // if there is a product state, then use that (this happens for PRIME flow)
  if (productState) {
    return {
      request: productState.productsRequest,
      response: productState.productsResponse as GetProductsResponse,
      gridProducts: productState.visibleProducts,
      filters: productState.filters,
      markup: productState.markup,
    };
  }

  // else recreate the product state from available options and standard response
  const getProductsResponse = new GetProductsResponse(payload.availableOptions, payload.StandardResponse);
  if (useQuoteOnlyEndpoint) {
    getProductsResponse.availableOptions.products.productItem = payload.cartState.cart.map(
      (cartItem: CartItem) => cartItem.product
    );
  }
  const filters = calculateFilters(getProductsResponse.availableOptions.products.productItem);

  const rawProducts = payload.availableOptions.products.productItem;
  const gridProducts = calculateCustomerCosts(productResponseDataMapper(rawProducts), markup, []);

  return {
    request: payload.getProductRequest,
    response: getProductsResponse,
    gridProducts,
    filters,
    markup,
  };
};

const hasValue = (value: unknown): boolean => value !== null && value !== undefined;

export const calculateCreditInsuranceMonthlyPayment = (
  financeType: FinanceType,
  values: CreditInsuranceForm
): Partial<CreditInsuranceForm> => {
  if (
    !hasValue(values.loanAmount) ||
    !hasValue(values.interestRate) ||
    !hasValue(values.amortizationPeriod) ||
    !hasValue(values.loanTerm)
  ) {
    console.error('Missing required values for calculateCreditInsuranceMonthlyPayment', values);
    return { monthlyPayment: '0.00', residualValue: '0' };
  }
  const financeAmount = +values.loanAmount!;
  const interestRate = +values.interestRate! / 100 / 12;
  const amortizationPeriod = +values.amortizationPeriod!;
  const loanTerm = +values.loanTerm!;

  return calculateMonthlyPayment<CreditInsuranceForm>(
    financeType,
    financeAmount,
    interestRate,
    loanTerm,
    amortizationPeriod
  );
};

export const calculateFinanceMonthlyPayment = (financeType: FinanceType, values: FinanceForm): Partial<FinanceForm> => {
  if (
    !hasValue(values.financeAmount) ||
    !hasValue(values.apr) ||
    !hasValue(values.amortizationPeriod) ||
    !hasValue(values.loanTerm)
  ) {
    console.error('Missing required values for calculateFinanceMonthlyPayment', values);
    return { monthlyPayment: '0.00', residualValue: '0' };
  }
  const financeAmount = +values.financeAmount!;
  const interestRate = +values.apr! / 100 / 12;
  const amortizationPeriod = +values.amortizationPeriod!;
  const loanTerm = +values.loanTerm!;

  return calculateMonthlyPayment<FinanceForm>(financeType, financeAmount, interestRate, loanTerm, amortizationPeriod);
};

export const calculateMonthlyPayment = <T extends { monthlyPayment: string | null; residualValue: string | null }>(
  financeType: FinanceType,
  financeAmount: number,
  interestRate: number,
  loanTerm: number,
  amortizationPeriod: number
): Partial<T> => {
  let totalMonthlyPayment = 0;
  let balloonResidual = 0;

  const result: Partial<T> = {};

  switch (financeType) {
    case FinanceType.Finance:
    case FinanceType.Lease:
      if (!financeInputValid(financeAmount, interestRate, loanTerm)) {
        totalMonthlyPayment = 0;
      } else {
        totalMonthlyPayment =
          interestRate === 0
            ? financeAmount / loanTerm
            : financeAmount /
              ((Math.pow(1 + interestRate, loanTerm) - 1) / (interestRate * Math.pow(1 + interestRate, loanTerm)));
      }

      result.monthlyPayment = totalMonthlyPayment?.toFixed(2);
      result.residualValue = Number(0).toFixed();

      break;
    case FinanceType.Balloon:
      if (!balloonInputValid(financeAmount, interestRate, loanTerm, amortizationPeriod)) {
        totalMonthlyPayment = 0;
        balloonResidual = 0;
      } else {
        totalMonthlyPayment =
          amortizationPeriod >= loanTerm
            ? interestRate === 0
              ? financeAmount / amortizationPeriod
              : (interestRate * financeAmount) / (1 - (1 + interestRate) ** -amortizationPeriod)
            : 0;
        balloonResidual =
          amortizationPeriod >= loanTerm
            ? interestRate === 0
              ? financeAmount * (1 - loanTerm / amortizationPeriod)
              : (totalMonthlyPayment * (1 - (1 + interestRate) ** -(amortizationPeriod - loanTerm))) / interestRate
            : 0;
      }

      result.monthlyPayment = totalMonthlyPayment?.toFixed(2);
      result.residualValue = balloonResidual?.toFixed(2);

      break;
    case FinanceType.Cash:
      result.monthlyPayment = Number(0).toFixed(2);
      result.residualValue = Number(0).toFixed(2);

      break;
    default:
      break;
  }

  return result;
};

export const financeInputValid = (financeAmount: number, interestRate: number, loanTerm: number): boolean =>
  !(isNaN(financeAmount) || isNaN(interestRate) || isNaN(loanTerm) || loanTerm <= 0);

export const balloonInputValid = (
  financeAmount: number,
  interestRate: number,
  loanTerm: number,
  amortizationPeriod: number
): boolean => {
  if (!financeInputValid(financeAmount, interestRate, loanTerm)) {
    return false;
  }

  return !(isNaN(amortizationPeriod) || amortizationPeriod <= 0 || amortizationPeriod < loanTerm);
};

export const pickProps = (obj: { [key: string]: unknown }, props: string[]) =>
  props.reduce((acc: { [key: string]: unknown }, prop: string) => {
    acc[prop] = obj[prop];

    return acc;
  }, {});

export const objectsEqual = (obj1: { [key: string]: unknown }, obj2: { [key: string]: unknown }): boolean =>
  JSON.stringify(obj1) === JSON.stringify(obj2);

export interface UpdateCheckoutFormContext {
  getCheckoutFormValues: () => CheckoutFormValues;
  patchCheckoutFormValues: (values: CheckoutFormValues) => void;
  vehicleStore: Pick<VehicleStoreType, 'financeForm' | 'values' | 'updateProductForm'>;
}

/**
 * Merges partial checkout updates, recalculates finance/credit-insurance monthly values when needed,
 * syncs vehicle finance fields, and patches checkout form state (including cross-form sync via getSynchronizedFinances).
 */
export function updateCheckoutForm(
  ctx: UpdateCheckoutFormContext,
  updatedFields: Partial<CheckoutFormValues>
): void {
  const vehicleFinance = ctx.vehicleStore.financeForm();
  const productForm = ctx.vehicleStore.values();
  const checkoutValues = ctx.getCheckoutFormValues();

  const financeType = vehicleFinance.financeType;
  const shouldCalculateMonthlyPayment =
    financeType && [FinanceType.Finance, FinanceType.Lease, FinanceType.Balloon].includes(financeType);

  if (updatedFields.financeForm || updatedFields.creditInsuranceForm) {
    const updatedFinance = shouldCalculateMonthlyPayment
      ? calculateFinanceMonthlyPayment(financeType, checkoutValues.financeForm)
      : { monthlyPayment: '0.00', residualValue: '0' };
    const creditInsuranceFormValues = {
      ...checkoutValues.creditInsuranceForm,
      ...updatedFields.creditInsuranceForm,
    } as CreditInsuranceForm;
    const updatedCreditInsurance = shouldCalculateMonthlyPayment
      ? calculateCreditInsuranceMonthlyPayment(financeType, creditInsuranceFormValues)
      : { monthlyPayment: '0.00', residualValue: '0' };

    if (updatedFields.financeForm) {
      updatedFields.financeForm = { ...updatedFields.financeForm, ...updatedFinance };
    }
    if (updatedFields.creditInsuranceForm) {
      updatedFields.creditInsuranceForm = { ...updatedFields.creditInsuranceForm, ...updatedCreditInsurance };
    }

    const comparedProps = ['apr', 'loanTerm', 'amortizationPeriod', 'residualValue'];
    const checkoutFinanceSubset = pickProps(
      { ...checkoutValues.financeForm, ...updatedFinance },
      comparedProps
    );
    const vehicleFinanceSubset = pickProps({ ...vehicleFinance }, comparedProps);
    if (!objectsEqual(checkoutFinanceSubset, vehicleFinanceSubset)) {
      const combinedValues: Partial<VehicleValues> = (
        productForm.apr
          ? { ...vehicleFinance, ...checkoutFinanceSubset, apr: productForm.apr }
          : { ...vehicleFinance, ...checkoutFinanceSubset }
      ) as Partial<VehicleValues>;
      ctx.vehicleStore.updateProductForm(combinedValues);
    }
  }

  ctx.patchCheckoutFormValues({
    ...ctx.getCheckoutFormValues(),
    ...getSynchronizedFinances(ctx.getCheckoutFormValues(), updatedFields),
  });
}
